import React from 'react';
import { Tag, Tooltip } from 'antd';
import {
    FileExcelOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    FileOutlined,
    FileMarkdownOutlined,
    LinkOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';

export const DOCUMENT_EXTENSIONS = ['txt', 'pdf', 'docx', 'md', 'doc'];
export const TABULAR_EXTENSIONS = ['csv', 'xlsx', 'xls'];
export const ALLOWED_EXTENSIONS = [...DOCUMENT_EXTENSIONS, ...TABULAR_EXTENSIONS];

export const getFileTypeIcon = (type, sourceType, size = 20) => {
    if (sourceType === 'webpage') return <LinkOutlined style={{ color: 'var(--color-primary)', fontSize: size }} />;
    if (!type) return <FileOutlined style={{ fontSize: size }} />;
    const t = type.toLowerCase();
    if (['csv', 'xlsx', 'xls'].includes(t)) return <FileExcelOutlined style={{ color: 'var(--color-success)', fontSize: size }} />;
    if (t === 'pdf') return <FilePdfOutlined style={{ color: 'var(--color-error)', fontSize: size }} />;
    if (t === 'md') return <FileMarkdownOutlined style={{ color: '#7c3aed', fontSize: size }} />;
    if (t === 'txt') return <FileTextOutlined style={{ color: 'var(--color-info)', fontSize: size }} />;
    if (['doc', 'docx'].includes(t)) return <FileWordOutlined style={{ color: 'var(--color-primary)', fontSize: size }} />;
    return <FileOutlined style={{ fontSize: size }} />;
};

export const isDocumentFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return DOCUMENT_EXTENSIONS.includes(ext);
};

export const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const STATUS_CONFIG = {
    completed: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed', dotColor: 'var(--color-success)' },
    processing: { color: 'processing', icon: <ClockCircleOutlined />, label: 'Processing', dotColor: 'var(--color-warning)' },
    pending: { color: 'default', icon: <ClockCircleOutlined />, label: 'Pending', dotColor: 'var(--color-text-muted)' },
    failed: { color: 'error', icon: <CloseCircleOutlined />, label: 'Failed', dotColor: 'var(--color-error)' },
};

export const EmbeddingStatusTag = ({ status }) => {
    const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return <Tag color={c.color} icon={c.icon}>{c.label}</Tag>;
};

export const StatusDot = ({ status }) => {
    const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
        <Tooltip title={c.label}>
            <span
                className={`status-dot ${status === 'processing' ? 'status-dot-pulse' : ''}`}
                style={{ '--dot-color': c.dotColor }}
            />
        </Tooltip>
    );
};

export const formatPageindexContext = (n) => {
    if (n == null || n === '') return null;
    const num = Number(n);
    if (Number.isNaN(num)) return null;
    if (num >= 1_000_000) return `${Math.round(num / 100000) / 10}M`;
    if (num >= 1000) return `${Math.round(num / 100) / 10}k`;
    return String(num);
};

/** Context window for chat catalog models (same rules as Add Assistant). */
export const formatChatCatalogContext = (tokens) => {
    if (tokens == null || tokens === '') return null;
    const t = Number(tokens);
    if (Number.isNaN(t)) return null;
    if (t >= 1_000_000) return `${(t / 1_000_000).toFixed(0)}M`;
    if (t >= 1_000) return `${(t / 1_000).toFixed(0)}K`;
    return `${t}`;
};

/** Approximate $/1M tokens from per-token cost (cloud catalog). */
export const formatChatCatalogCostPerMillion = (costPerToken) => {
    if (costPerToken == null || costPerToken === '') return null;
    const c = Number(costPerToken);
    if (Number.isNaN(c)) return null;
    const perMillion = c * 1_000_000;
    if (perMillion < 0.01) return '<$0.01/M';
    return `$${perMillion.toFixed(2)}/M`;
};

/** Shared by collection tables and document chunk preview pagination. */
export const DATA_COLLECTIONS_PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

/** Ant Design Select props for page-size dropdown: no search field, theme classes. */
export const DATA_COLLECTIONS_SIZE_CHANGER_SELECT_PROPS = {
    showSearch: false,
    inputReadOnly: true,
    className: 'collections-pagination-page-size',
    popupClassName: 'collections-pagination-page-size-dropdown',
    popupMatchSelectWidth: false,
    listHeight: 256,
};

/**
 * Table pagination for Data Collections UIs: theme-aligned page-size control, no typing / search in the dropdown.
 */
export const getDataCollectionsTablePagination = (defaultPageSize, { totalItemLabel } = {}) => ({
    defaultPageSize,
    pageSizeOptions: DATA_COLLECTIONS_PAGE_SIZE_OPTIONS,
    size: 'small',
    showSizeChanger: DATA_COLLECTIONS_SIZE_CHANGER_SELECT_PROPS,
    showTotal:
        totalItemLabel == null
            ? undefined
            : (total, range) =>
                  React.createElement(
                      'span',
                      { className: 'collections-pagination-total' },
                      `${range[0].toLocaleString()}–${range[1].toLocaleString()} of ${total.toLocaleString()} ${totalItemLabel}`
                  ),
});

/** Controlled server-side pagination for Ant Design Table. */
export const getServerTablePagination = ({
    current,
    pageSize,
    total,
    onChange,
    totalItemLabel,
}) => ({
    current,
    pageSize,
    total,
    onChange,
    pageSizeOptions: DATA_COLLECTIONS_PAGE_SIZE_OPTIONS,
    size: 'small',
    showSizeChanger: DATA_COLLECTIONS_SIZE_CHANGER_SELECT_PROPS,
    showTotal:
        totalItemLabel == null
            ? undefined
            : (count, range) =>
                  React.createElement(
                      'span',
                      { className: 'collections-pagination-total' },
                      `${range[0].toLocaleString()}–${range[1].toLocaleString()} of ${count.toLocaleString()} ${totalItemLabel}`
                  ),
});
